import * as React from "react";
import { useState } from "react";
import { ArrowDownload20Regular, ArrowSync20Regular, ArrowUpload24Regular, Checkmark20Regular, Delete20Regular, Dismiss20Regular, ErrorCircle20Regular } from "@fluentui/react-icons";
import { DataSource } from "../../../types/DataSource";
import { IAttachedFile } from "../../../../redux/types/IAttachedFile";
import { IAddDistributionAttachmentModel } from "../../../../redux/types/IAddDistributionAttachmentModel";
import { IRemoveDistributionAttachment } from "../../../types/IRemoveDistributionBlobAttachment";
import { RepertoireService } from "../../../../redux/services/RepertoireService";
import { StorageService } from "../../../../dataingestion/services/StorageService";

export interface IModalFileUploadProps {
    entityId: number;
    entityType: 'distribution' | 'pool' | 'work' | 'product' | 'agreement'; // Extensible for future use
    attachedFiles?: IAttachedFile[];
    onFileUploaded?: (file: IAttachedFile) => void;
    onFileRemoved?: (file: IAttachedFile) => void;
    isReadOnly?: boolean;
    title?: string;
}

interface FileStatus {
    uploading: boolean;
    success: boolean;
    error: boolean;
    message?: string;
}

export const ModalFileUpload: React.FunctionComponent<IModalFileUploadProps> = ({
    entityId,
    entityType,
    attachedFiles = [],
    onFileUploaded,
    onFileRemoved,
    isReadOnly = false,
    title = "Attachments"
}) => {
    const [fileStatuses, setFileStatuses] = useState<{ [fileName: string]: FileStatus }>({});
    const [downloadingFiles, setDownloadingFiles] = useState<{ [fileGuid: string]: boolean }>({});

    const updateFileStatus = (fileName: string, status: Partial<FileStatus>) => {
        setFileStatuses(prev => ({
            ...prev,
            [fileName]: { ...prev[fileName], ...status }
        }));
    };

    const getAttachmentModel = (fileName: string): IAddDistributionAttachmentModel => {
        // For now, we'll use the distribution attachment model but set the appropriate property
        // This can be extended in the future for other entity types
        const attachedFile: any = {
            fileName: fileName,
        };

        switch (entityType) {
            case 'distribution':
            case 'pool':
                attachedFile.distributionPoolId = entityId;
                break;
            case 'work':
                attachedFile.workId = entityId;
                break;
            case 'product':
                attachedFile.productCoreID = entityId;
                break;
            case 'agreement':
                attachedFile.agreementId = entityId;
                break;
            default:
                attachedFile.distributionPoolId = entityId;
        }

        return {
            attachedFile,
            dataSource: DataSource.Repertoire,
        };
    };

    const getStoragePath = (guid: string): string => {
        switch (entityType) {
            case 'distribution':
            case 'pool':
                return `repertoireAttachments/distribution/${guid}`;
            case 'work':
                return `repertoireAttachments/work/${guid}`;
            case 'product':
                return `repertoireAttachments/product/${guid}`;
            case 'agreement':
                return `repertoireAttachments/agreement/${guid}`;
            default:
                return `repertoireAttachments/distribution/${guid}`;
        }
    };

    const getRemovalModel = (attachedFile: IAttachedFile): IRemoveDistributionAttachment => {
        return {
            Guid: attachedFile.guid,
            Source: DataSource.Repertoire,
            distributionID: entityId, // This property name might need to be generic in the future
        };
    };

    const handleFileUpload = async (files: FileList) => {
        if (!files || files.length === 0 || isReadOnly) return;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileName = file.name;

            updateFileStatus(fileName, { uploading: true, success: false, error: false });

            try {
                const model = getAttachmentModel(fileName);
                const attachedFile = await RepertoireService.addDistributionAttachmentRepertoire(model);

                const path = getStoragePath(attachedFile.guid);
                const uploadUri = await StorageService.getUploadUri(path, true);
                
                if (uploadUri && uploadUri.length > 0 && (await StorageService.uploadFile(uploadUri, file))) {
                    updateFileStatus(fileName, { 
                        uploading: false, 
                        success: true, 
                        error: false, 
                        message: `${fileName} uploaded successfully!` 
                    });
                    onFileUploaded && onFileUploaded(attachedFile);
                }
                else {
                    updateFileStatus(fileName, { 
                        uploading: false, 
                        success: false, 
                        error: true, 
                        message: `Failed to get upload URL for ${fileName}` 
                    });
                    await RepertoireService.removeDistributionAttachment({
                        attachedFile: { 
                            FileName: fileName
                        } as any,
                        dataSource: DataSource.Repertoire,
                    });
                }
            }
            catch (error) {
                updateFileStatus(fileName, { 
                    uploading: false, 
                    success: false, 
                    error: true, 
                    message: `Error uploading ${fileName}` 
                });
            }
        }
    };

    const handleFileRemove = async (attachedFile: IAttachedFile) => {
        if (isReadOnly) return;

        try {
            const model = getRemovalModel(attachedFile);

            RepertoireService.removeDistributionBlobAttachment(model).then((res) => {
                if (res) {
                    RepertoireService.removeDistributionAttachment({
                        attachedFile: { 
                            AttachedFileID: attachedFile.attachedFileID,
                            FileName: attachedFile.guid
                        } as any,
                        dataSource: DataSource.Repertoire,
                    }).then((removed: boolean) => {
                        if (removed) {
                            onFileRemoved && onFileRemoved(attachedFile);
                        }
                    });
                }
            });
        }
        catch (error) {
        }
    };

    const handleFileDownload = async (attachedFile: IAttachedFile) => {
        try {
            setDownloadingFiles(prev => ({ ...prev, [attachedFile.guid]: true }));
            
            const path = getStoragePath(attachedFile.guid);
            const downloadUri = await StorageService.downloadFile(path, true, attachedFile.fileName);
            
            if (downloadUri && downloadUri.length > 0) {
                window.open(downloadUri, '_blank');
            } else {
                console.error('Failed to get download URL for file:', attachedFile.fileName);
            }
        } catch (error) {
            console.error('Error downloading file:', attachedFile.fileName, error);
        } finally {
            setDownloadingFiles(prev => ({ ...prev, [attachedFile.guid]: false }));
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isReadOnly) return;
        
        const files = e.dataTransfer.files;
        handleFileUpload(files);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFileUpload(e.target.files);
            e.target.value = "";
        }
    };

    return (
        <div className="modal-file-upload">
            <h4>{title}</h4>
            
            {!isReadOnly && (
                <div
                    className="file-drop-zone"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    style={{
                        border: "2px dashed #ccc",
                        borderRadius: "4px",
                        padding: "20px",
                        textAlign: "center",
                        marginBottom: "10px",
                        cursor: "pointer"
                    }}
                    onClick={() => document.getElementById('file-input')?.click()}
                >
                    <ArrowUpload24Regular style={{ marginBottom: "10px" }} />
                    <div>Drop files here or click to browse</div>
                    <input
                        id="file-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                    />
                </div>
            )}

            {Object.keys(fileStatuses).length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                    {Object.keys(fileStatuses).map(fileName => {
                        const status = fileStatuses[fileName];
                        return (
                            <div 
                                key={fileName} 
                                style={{ 
                                    padding: "5px", 
                                    backgroundColor: status.success ? "#d4edda" : status.error ? "#f8d7da" : "#f0f0f0", 
                                    marginBottom: "5px",
                                    border: status.success ? "1px solid #c3e6cb" : status.error ? "1px solid #f5c6cb" : "1px solid #ddd",
                                    borderRadius: "4px"
                                }}
                            >
                                {status.uploading && (
                                    <>
                                        <ArrowSync20Regular className="syncOccurrenceIcon" /> Uploading {fileName}...
                                    </>
                                )}
                                {status.success && (
                                    <>
                                        <Checkmark20Regular style={{ color: "#155724" }} /> {status.message}
                                        <Dismiss20Regular
                                            style={{ 
                                                color: "#155724", 
                                                cursor: "pointer", 
                                                marginLeft: "10px", 
                                                float: "right" 
                                            }}
                                            onClick={() => {
                                                setFileStatuses(prev => {
                                                    const newStatuses = { ...prev };
                                                    delete newStatuses[fileName];
                                                    return newStatuses;
                                                });
                                            }}
                                            title="Dismiss success message"
                                        />
                                    </>
                                )}
                                {status.error && (
                                    <>
                                        <ErrorCircle20Regular style={{ color: "#721c24" }} /> {status.message}
                                        <Dismiss20Regular
                                            style={{ 
                                                color: "#721c24", 
                                                cursor: "pointer", 
                                                marginLeft: "10px", 
                                                float: "right" 
                                            }}
                                            onClick={() => {
                                                setFileStatuses(prev => {
                                                    const newStatuses = { ...prev };
                                                    delete newStatuses[fileName];
                                                    return newStatuses;
                                                });
                                            }}
                                            title="Dismiss error"
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {attachedFiles.length > 0 && (
                <div className="file-list">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>File Name</th>
                                <th>Last Modified</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attachedFiles.map((file, index) => (
                                <tr key={index}>
                                    <td>{file.fileName}</td>
                                    <td>{file.lastModifiedDate ? new Date(file.lastModifiedDate).toLocaleDateString() : ''}</td>
                                    <td>
                                        <div>
                                            {downloadingFiles[file.guid]
                                                ? <span title="Downloading..." style={{ cursor: 'default', marginRight: '10px', opacity: 0.6 }}>
                                                    <ArrowSync20Regular className="icon node-icon syncOccurrenceIcon" />
                                                  </span>
                                                : <span title="Download file" style={{ cursor: 'pointer', marginRight: '10px', opacity: 1 }} onClick={() => handleFileDownload(file)}>
                                                    <ArrowDownload20Regular className="icon node-icon" />
                                                  </span>}
                                            {!isReadOnly && (
                                                <span title="Remove attachment" style={{ cursor: 'pointer' }} onClick={() => handleFileRemove(file)}>
                                                    <Delete20Regular className="icon node-icon" />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ModalFileUpload;